import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@cureskin/api-client';
import { ConnectionService } from '../../../../services/connection-service';
import { Broadcaster } from '../../../../components/broadcaster';
import { InputType } from '../../../../../typings/client/input';
import { AppErrorType, AppErrorTypeMessage } from '../../../app-error.constants';

@Component({
  selector: 'clinical-report-edit',
  templateUrl: './edit.html',
  standalone: false,
})
export class ClinicalReportEditComponent {
  clinicalReportId: string;
  clinicalReport: Models.ClinicalReport;
  usedForOptions: Array<InputType.SelectOption> = [];
  analysisForDoctorJSON: string = '';

  constructor(
    private connectionService: ConnectionService,
    private route: ActivatedRoute,
    private router: Router,
    private broadcaster: Broadcaster) {
  }

  async ngOnInit(): Promise<void> {
    this.usedForOptions = Object.values(Models.ClinicalReportUsedForEnum)
      .map((value: Models.ClinicalReportUsedForEnum): InputType.SelectOption => ({ display: value, value }));
    this.clinicalReportId = this.route.snapshot.params.id;
    if (this.clinicalReportId) {
      const response: Models.FindClinicalReportByIdResponse = await this.connectionService.findClinicalReportById(this.clinicalReportId);
      this.clinicalReport = response.clinicalReport || {};
    } else {
      this.clinicalReport = { usedFor: Models.ClinicalReportUsedForEnum.BOTH, active: true };
    }
    this.analysisForDoctorJSON = this.clinicalReport.analysisForDoctor
      ? JSON.stringify(this.clinicalReport.analysisForDoctor, undefined, 2) : '';
  }

  async saveClinicalReport(): Promise<void> {
    try {
      const data: Models.CreateClinicalReportRequest = {
        mainConcern: this.clinicalReport.mainConcern,
        usedFor: this.clinicalReport.usedFor,
        active: this.clinicalReport.active,
        facewash: this.languageStringId('facewash'),
        moisturiser: this.languageStringId('moisturiser'),
        repair: this.languageStringId('repair'),
        analysisForUser: this.languageStringId('analysisForUser'),
        analysisForDoctor: this.parseAnalysisForDoctor(),
      };
      if (this.clinicalReportId) {
        await this.connectionService.updateClinicalReport(this.clinicalReportId, data);
      } else {
        await this.connectionService.createClinicalReport(data);
      }
      this.broadcaster.broadcast('NOTIFY', { message: AppErrorTypeMessage.SAVED_SUCCESSFULLY, type: AppErrorType.SUCCESS });
      this.router.navigate(['products/clinicalReport']);
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: error.message, type: AppErrorType.ERROR });
    }
  }

  private languageStringId(field: 'facewash' | 'moisturiser' | 'repair' | 'analysisForUser'): string | undefined {
    const value: unknown = this.clinicalReport[field];
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    return (value as Models.LanguageString).objectId;
  }

  private parseAnalysisForDoctor(): Array<{ [key: string]: unknown }> {
    if (!this.analysisForDoctorJSON?.trim()) {
      return [];
    }
    const parsed: unknown = JSON.parse(this.analysisForDoctorJSON);
    if (!Array.isArray(parsed)) {
      throw new Error('Analysis For Doctor must be a JSON array of objects');
    }
    return parsed as Array<{ [key: string]: unknown }>;
  }
}
